// Welds geometrically-equivalent vertices within a single primitive so MeshoptSimplifier
// can see real connectivity across the seams that attribute-split (hard-shaded / UV-seam)
// GLB export introduces - it collapses triangle-adjacency purely through shared vertex
// index identity, so duplicated-but-coincident vertices otherwise look disconnected.
// Never called across primitives: material/primitive boundaries are what keep this safe.
const DEFAULT_NORMAL_MIN_DOT = 0.9999

export function weldPrimitiveVertices({ positions, normals, uvs, indices }, options = {}) {
  const positionEpsilon = options.positionEpsilon ?? 0
  const normalMinDot = options.normalMinDot ?? DEFAULT_NORMAL_MIN_DOT
  const vertexCount = positions.length / 3

  // 1) Bucket vertices by (quantized) position. positionEpsilon = 0 means exact float32
  //    equality, which is sufficient whenever the source has no near-miss position noise.
  const buckets = new Map()
  for (let v = 0; v < vertexCount; v += 1) {
    const key = positionKey(positions, v, positionEpsilon)
    let bucket = buckets.get(key)
    if (!bucket) buckets.set(key, bucket = [])
    bucket.push(v)
  }

  // 2) Within each position bucket, greedily cluster by normal similarity (+ exact UV
  //    match when UVs are present). Greedy-first-fit in original vertex order keeps this
  //    deterministic without an O(n^2) global comparison; bucket sizes are small in
  //    practice (a handful of split corners per position), so this stays close to O(n).
  const canonical = new Uint32Array(vertexCount)
  const representatives = []
  for (const bucket of buckets.values()) {
    const clusters = []
    for (const v of bucket) {
      const cluster = clusters.find(c => normalsClose(normals, v, c.representative, normalMinDot) && uvsEqual(uvs, v, c.representative))
      if (cluster) {
        canonical[v] = cluster.representative
      } else {
        clusters.push({ representative: v })
        canonical[v] = v
        representatives.push(v)
      }
    }
  }

  // 3) Compact: only canonical "survivor" vertices make it into the output buffers, in
  //    ascending original-index order, so output ordering is deterministic and stable.
  representatives.sort((a, b) => a - b)
  const newIndexOf = new Map(representatives.map((v, i) => [v, i]))

  const weldedVertices = representatives.length
  const weldedPositions = new Float32Array(weldedVertices * 3)
  const weldedNormals = new Float32Array(weldedVertices * 3)
  const weldedUvs = uvs ? new Float32Array(weldedVertices * 2) : null
  representatives.forEach((v, i) => {
    weldedPositions[i * 3] = positions[v * 3]
    weldedPositions[i * 3 + 1] = positions[v * 3 + 1]
    weldedPositions[i * 3 + 2] = positions[v * 3 + 2]
    weldedNormals[i * 3] = normals[v * 3]
    weldedNormals[i * 3 + 1] = normals[v * 3 + 1]
    weldedNormals[i * 3 + 2] = normals[v * 3 + 2]
    if (weldedUvs) {
      weldedUvs[i * 2] = uvs[v * 2]
      weldedUvs[i * 2 + 1] = uvs[v * 2 + 1]
    }
  })

  // 4) Remap the index buffer through the canonical map. Corner order (a,b,c) is never
  //    reordered - only the index values change - so winding is untouched. Any triangle
  //    welding collapsed into a repeated-vertex triangle is dropped and counted.
  const weldedIndices = []
  let degenerateTrianglesRemoved = 0
  for (let i = 0; i < indices.length; i += 3) {
    const a = newIndexOf.get(canonical[indices[i]])
    const b = newIndexOf.get(canonical[indices[i + 1]])
    const c = newIndexOf.get(canonical[indices[i + 2]])
    if (a === b || b === c || a === c) {
      degenerateTrianglesRemoved += 1
      continue
    }
    weldedIndices.push(a, b, c)
  }

  return {
    indices: new Uint32Array(weldedIndices),
    positions: weldedPositions,
    normals: weldedNormals,
    uvs: weldedUvs,
    sourceVertices: vertexCount,
    weldedVertices,
    degenerateTrianglesRemoved,
  }
}

function positionKey(positions, v, epsilon) {
  const x = positions[v * 3]
  const y = positions[v * 3 + 1]
  const z = positions[v * 3 + 2]
  if (!epsilon) return `${x},${y},${z}`
  return `${Math.round(x / epsilon)},${Math.round(y / epsilon)},${Math.round(z / epsilon)}`
}

function normalsClose(normals, a, b, minDot) {
  const dot = normals[a * 3] * normals[b * 3]
    + normals[a * 3 + 1] * normals[b * 3 + 1]
    + normals[a * 3 + 2] * normals[b * 3 + 2]
  return dot >= minDot
}

function uvsEqual(uvs, a, b) {
  if (!uvs) return true
  return uvs[a * 2] === uvs[b * 2] && uvs[a * 2 + 1] === uvs[b * 2 + 1]
}
